/**
 * Default IQP / company for a whole SS (building_ss_instances) ?? applies to all units under that SS.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import UnitDefaultAssignee, { assigneeFromUnit } from './UnitDefaultAssignee';

function toastAssigneeResult(data) {
  if (data?.notify?.stubbed || data?.notify?.messageId) {
    toast.success('Default IQP saved for this SS ?? notification sent (or logged if SMTP off)');
  } else if (data?.notify?.unchanged) {
    toast.success('Default IQP unchanged');
  } else if (data?.notify?.skipped) {
    toast.success('Default IQP cleared for this SS');
  } else {
    const n = data?.cascaded_units;
    toast.success(
      n != null && n > 0
        ? `Default IQP saved for this SS (${n} unit(s) updated)`
        : 'Default IQP saved for this SS'
    );
  }
}

export default function SsDefaultAssignee({
  buildingId,
  instanceId,
  ssCode,
  compact = false,
  instancePack = null,
  onSaved,
}) {
  const queryClient = useQueryClient();

  const { data: fetched } = useQuery({
    queryKey: ['ss-instance', buildingId, instanceId],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/ss-instances/${instanceId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!buildingId && !!instanceId,
  });

  // Prefer live query (refreshes after save); fall back to parent pack while loading
  const instance = fetched || instancePack;

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.put(`/buildings/${buildingId}/ss-instances/${instanceId}`, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toastAssigneeResult(data);
      queryClient.invalidateQueries({ queryKey: ['ss-instance', buildingId, instanceId] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'ss-units'] });
      onSaved?.(data);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save default IQP'),
  });

  if (!buildingId || !instanceId) {
    return null;
  }

  return (
    <UnitDefaultAssignee
      scope="ss"
      ssCode={ssCode || instance?.ss_code}
      compact={compact}
      value={assigneeFromUnit(instance || {})}
      saving={saveMutation.isPending}
      onSave={(next) => saveMutation.mutateAsync(next)}
    />
  );
}
